import { EntityBase } from '@provision/entity-types';
import { KeyboardEvent, MouseEvent, useState } from 'react';
import type { RowSelectionState } from 'src/pages/CustomViews/custom-views-entities-table/row-select-checkbox';
import type { RecordClickHandler } from 'src/pages/CustomViews/hooks/use-debounced-record-click';
import { LongPressEventType, useLongPress } from 'use-long-press';

const SELECT_LONG_PRESS_MS = 400;
const SELECT_LONG_PRESS_MOVE_TOLERANCE_PX = 12;

/**
 * A tap opens the record, or toggles it while a selection is going on. A long press on a card
 * starts that selection, and the click the browser may still send on release is swallowed so
 * the press does not also open (or immediately unselect) the record.
 */
export const useCardInteraction = <T extends EntityBase>({
  record,
  isSelectionMode,
  selectionState,
  onOpen,
  isSwiping,
}: {
  record: T;
  isSelectionMode: boolean;
  selectionState?: RowSelectionState;
  onOpen?: RecordClickHandler<T>;
  isSwiping?: () => boolean;
}) => {
  const [isReleaseClickSwallowed, setIsReleaseClickSwallowed] = useState(false);

  const id = String(record.id);
  const isSelected = !!selectionState?.isRowSelected(id);

  const longPressHandlers = useLongPress(
    selectionState && !isSelectionMode
      ? () => {
          // a swipe starts at 8px and the press only gives up at 12px, so a held swipe can reach here
          if (isSwiping?.()) return;

          setIsReleaseClickSwallowed(true);
          selectionState.onRowSelectionChange(id, true);
        }
      : null,
    {
      threshold: SELECT_LONG_PRESS_MS,
      detect: LongPressEventType.Touch,
      cancelOnMovement: SELECT_LONG_PRESS_MOVE_TOLERANCE_PX,
    },
  );

  const activate = (event?: MouseEvent) => {
    if (isSelectionMode && selectionState) {
      selectionState.onRowSelectionChange(id, !isSelected);
      return;
    }

    onOpen?.(record.id, record, event);
  };

  const handleClick = (event: MouseEvent) => {
    if (isReleaseClickSwallowed) {
      setIsReleaseClickSwallowed(false);
      return;
    }

    activate(event);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      activate();
    }
  };

  return {
    isSelected,
    rootProps: {
      ...longPressHandlers(),
      onClick: handleClick,
      onKeyDown: handleKeyDown,
      // the capture phase keeps this off the long-press handler's own onTouchStart prop
      onTouchStartCapture: () => setIsReleaseClickSwallowed(false),
    },
  };
};
